import { useQuery } from "react-query";

import { useMunicipalContext } from "contexts/MunicipalContext";
import { useUserContext } from "contexts/UserContext";
import { roles } from "constants/user";

import * as userService from "api/services/userService";

/**
 * Store server's state of municipal user lookups
 *
 * @returns The query object returned by the useQuery hook
 */
export const useMunicipalUserHooks = () => {
  const { user } = useUserContext();
  const shouldUseMunicipalityId =
    user &&
    (user.roles.includes(roles.municipalTreasurer) ||
      user.roles.includes(roles.municipalTreasurerDelegate));
  const municipalityId = shouldUseMunicipalityId ? user.municipalityId : "";

  const {
    getMunicipalityName,
    isFetching: isFetchingMunicipality,
  } = useMunicipalContext();

  return useQuery(
    ["users", "municipality", municipalityId],
    () => userService.getMunicipalUsers(municipalityId),
    {
      select: ({ data }) =>
        data.map((item) => ({
          id: item.id,
          name: item.name,
          municipality: getMunicipalityName(item.municipalityId),
          email: item.email,
          phoneExtension: item.phoneExtension,
          phone: item.phone,
          permissions: item.permissions.join(", "),
          enabled: item.enabled === 1,
          lastLoginDate: item.lastLoginDate
            ? item.lastLoginDate.split(".")[0].replace(/[+-]\d{2}:\d{2}$/, "")
            : null,
        })),
      enabled: !!user && !isFetchingMunicipality,
    }
  );
};

/**
 * Store server's state of treasurer user lookups
 *
 * @returns The query object returned by the useQuery hook
 */
export const useTreasurerUserHooks = () => {
  const {
    municipalities,
    isFetching: isFetchingMunicipality,
  } = useMunicipalContext();

  return useQuery(["users", "treasurer"], userService.getTreasurers, {
    select: ({ data }) =>
      data.map((item) => ({
        id: item.id,
        imisId: item.imisId,
        name: item.name,
        municipality: municipalities[item.municipalityId]?.name,
        email: item.email,
        phone: item.phone,
        phoneExtension: item.phoneExtension,
        enabled: item.enabled === 1,
        lastLoginDate: item.lastLoginDate
          ? item.lastLoginDate.split(".")[0].replace(/[+-]\d{2}:\d{2}$/, "")
          : null,
      })),
    enabled: !isFetchingMunicipality,
  });
};

/**
 * Store server's state of communications user lookups
 *
 * @returns The query object returned by the useQuery hook
 */
export const useCommunicationsUserHooks = () =>
  useQuery(["users", "communications"], userService.getCommunicationsUsers, {
    select: ({ data }) =>
      data.map((item) => ({
        ...item,
        lastLoginDate: item.lastLoginDate
          ? item.lastLoginDate.split(".")[0].replace(/[+-]\d{2}:\d{2}$/, "")
          : null,
        phone: item.phone,
        phoneExtension: item.phoneExtension,

        enabled: item.enabled === 1,
      })),
  });

/**
 * Store server's state of amo user lookups for User Management
 *
 * @returns The query object returned by the useQuery hook
 */
export const useAmoUserEnabledOrAdminHooks = () =>
  useQuery(["users", "municipal"], userService.getAmoUsersEnabledOrAdmin, {
    select: ({ data }) =>
      data.map((item) => ({
        ...item,
        lastLoginDate: item.lastLoginDate
          ? item.lastLoginDate.split(".")[0].replace(/[+-]\d{2}:\d{2}$/, "")
          : null,
        enabled: item.enabled === 1,
      })),
  });
